import { useMemo } from 'react'
import * as Yup from 'yup'
import { MultiStepFormStep as FormStep, Text } from '@percent/lemonade'
import { ReverificationAgent } from '@percent/cause-dashboard/common/hooks'
import { Organisation } from '@percent/cause-dashboard/services/cause/causeService.types'
import { Trans, useTranslation } from 'react-i18next'
import { agreementField, marketingConsentField, passwordAndConsentSchemas, passwordField, termsLinks } from '../signUpFields'
import { ReverificationAgentCard } from './ReverificationAgentCard'
import { ReverificationOrganisationCard } from './ReverificationOrganisationCard'
import { OrganisationDetailsDraft, OrganisationDetailsErrors } from './organisationDetails'

type UseReverificationStepProps = {
  organisation: Organisation | null
  agent: ReverificationAgent
  organisationDraft: OrganisationDetailsDraft
  onOrganisationDraftChange: (draft: OrganisationDetailsDraft) => void
  organisationErrors: OrganisationDetailsErrors
  isEditingOrganisation: boolean
  onEditingOrganisationChange: (isEditing: boolean) => void
}

export const useReverificationStep = ({
  organisation,
  agent,
  organisationDraft,
  onOrganisationDraftChange,
  organisationErrors,
  isEditingOrganisation,
  onEditingOrganisationChange
}: UseReverificationStepProps): FormStep => {
  const { t } = useTranslation()

  return useMemo(
    () => ({
      stepTitle: t('form.reverification.title'),
      stepSubtitle: (
        <>
          <Text size="small">{t('form.reverification.subtitle')}</Text>
          {organisation && (
            <ReverificationOrganisationCard
              organisation={organisation}
              value={organisationDraft}
              onChange={onOrganisationDraftChange}
              errors={organisationErrors}
              isEditing={isEditingOrganisation}
              onEditingChange={onEditingOrganisationChange}
            />
          )}
          <ReverificationAgentCard agent={agent} />
        </>
      ),
      stepTitleSize: 'h2',
      fields: [
        passwordField(t, t('form.reverification.passwordHint')),
        agreementField(<Trans i18nKey="form.reverification.agreement" components={termsLinks(t)} />),
        marketingConsentField(t)
      ],
      validationSchema: Yup.object().shape(passwordAndConsentSchemas(t)),
      buttonText: t('button.verifyMyAccount')
    }),
    [
      t,
      organisation,
      agent,
      organisationDraft,
      onOrganisationDraftChange,
      organisationErrors,
      isEditingOrganisation,
      onEditingOrganisationChange
    ]
  )
}
